<template>
  <div
    class="reservation"
    :style="serviceColor"
    @click="stopEventPropagation">
    <div class="date-time">
      <template v-if="daysMode">
        <span class="duration-range">
          <span>
            <strong>{{ $t('day.from') }}</strong>
            {{ reservationDateStart }}
          </span><br>
          <span>
            <strong>{{ $t('day.to') }}</strong>
            {{ reservationDateEnd }}
          </span>
        </span>
      </template>
      <template v-else>
        <span class="date">
          {{ reservationDate }}
        </span>
        <span class="time">
          <hour :value="itemData.time" :hasBorder="false" /> -
          <hour :value="timeTo" :hasBorder="false" />
        </span>
      </template>
    </div>

    <span
      v-if="workersEnabled && itemData.worker && itemData.worker.name"
      class="worker"
      :title="itemData.worker.name">
      <avatar
        :worker="itemData.worker"
        :name="itemData.worker.name" />
    </span>

    <div class="customer">
      <strong>{{ itemData.user_name }}</strong>

      <div class="details">
        <span
          v-if="peopleNumberEnabled"
          class="people-number">
          <icon
            name="profile-big"
            primaryColor="color-text-light"
            size="xxs" />
          <span class="people-number-counter">{{ itemData.people_number }}</span>
        </span>

        <a
          v-if="itemData.phone && userCanSeeContactData"
          href="#"
          :data-tel="itemData.phone"
          @click.prevent="$window.location.href='tel:' + itemData.phone;"
          class="phone">
          <icon
            name="phone"
            primaryColor="color-text-light"
            size="xxs" />
          <tooltip
            :text="$t('searchPopupReservation.phonePrefix') + itemData.phone"
            position="top" />
        </a>

        <a
          v-if="itemData.confirm_email && userCanSeeContactData"
          href="#"
          :data-email="itemData.confirm_email"
          :title="email"
          @click.prevent="$window.location.href='mailto:' + itemData.confirm_email;"
          class="email text-cut">
          <icon
            name="mailbox"
            primaryColor="color-text-light"
            size="xxs" />
          <tooltip
            :text="$t('searchPopupReservation.emailPrefix') + itemData.confirm_email"
            position="top" />
        </a>

        <span
          v-if="itemData.customer_id && userCanSeeContactData"
          class="client-info"
          @click="$bus.$emit('show-customer-popup', itemData.customer_id)">
          <icon
            name="info-circle-alt"
            primaryColor="color-text-light"
            strokeColor="color-text-light"
            size="xxs" />
          <tooltip :text="$t('common.ui.clientInfo')" />
        </span>

        <periodicity-viewer
          :itemData="itemData"
          :infoBoxPosition="'bottom'"
          :parentSelectorWithOverflow="'.search-popup .results'"
          :rowMode="false"
          :contentMode="'simple'"
          :autoRenew="itemData.periodicity_auto_renew" />
      </div>
    </div>

    <div
      v-if="(paymentsEnabled || servicesEnabled) && (itemData.service || itemData.payment)"
      class="additional-details">
      <div class="price">
        <span
          v-if="itemData.service.name && servicesEnabled"
          :class="{ 'service': true, 'no-price': !(paymentsEnabled && hasPrice) }">
          {{ itemData.service.name }}

          <tooltip
            v-if="itemData.service.category"
            :text="$t('searchPopupReservation.category') + itemData.service.category"
            :position="itemIndex === 0 ? 'bottom' : 'top'" />
        </span>

        <span
          v-if="paymentsEnabled && hasPrice && userCanSeeFullPaymentsData"
          class="total-price">
          <template v-if="itemData.has_periodicity && itemData.periodicity_payment_type === 0">
            {{ $t('common.ui.fullCycleCost') }} {{ totalPrice }}
          </template>
          <template v-else>
            {{ $t('common.ui.cost') }} {{ totalPrice }}
          </template>
        </span>

        <br v-if="userCanSeeFullPaymentsData && paymentsEnabled && hasPrice" />

        <span
          v-if="paymentsEnabled && hasPrice"
          class="payment-status">
          <span :class="paymentStatusClass">{{ paymentStatus }}</span>
        </span>
      </div>
    </div>

    <div class="actions">
      <btn
        :type="presentInProgress ? 'loader icon' : 'icon'"
        :title="$t('searchPopupReservation.clientNotPresent')"
        @click.native.prevent.stop="clientNotPresent()"
        :disabled="rejectInProgress || presentInProgress"
        :iconName="!presentInProgress ? 'thumbs-down-circle' : 'loader'"
        iconColor="color-warning" />
      <btn
        v-if="userCanAddReservation"
        :type="rejectInProgress ? 'loader icon' : 'icon'"
        :title="$t('searchPopupReservation.cancelReservation')"
        @click.native="reject"
        :disabled="rejectInProgress || presentInProgress"
        :iconName="!rejectInProgress ? 'cross-circle' : 'loader'"
        iconColor="color-danger" />
      <btn
        type="icon"
        :title="$t('searchPopupReservation.readMoreAboutReservation')"
        :disabled="rejectInProgress || presentInProgress"
        iconName="info-circle"
        iconColor="color-text-light"
        @click.native="showReservationDetails" />
    </div>
  </div>
</template>

<script>
import Avatar from './../common/Avatar';
import CalendarUtils from './../helpers/CalendarUtils';
import Configuration from './../mixins/Configuration';
import CurrencyUtils from './../helpers/CurrencyUtils';
import ErrorUtils from './../helpers/ErrorUtils.js';
import Hour from './../common/Hour';
import PeriodicityViewer from '../common/PeriodicityViewer.vue';
import Tooltip from './../common/Tooltip';
import UserTypes from './../mixins/UserTypes';

export default {
  name: 'search-popup-reservation',
  mixins: [
    Configuration,
    UserTypes
  ],
  props: [
    'itemData',
    'itemIndex'
  ],
  components: {
    'avatar': Avatar,
    'hour': Hour,
    'periodicity-viewer': PeriodicityViewer,
    'tooltip': Tooltip
  },
  data () {
    return {
      rejectInProgress: false,
      presentInProgress: false
    };
  },
  computed: {
    daysMode () {
      return this.itemData.type === 1;
    },
    paymentsEnabled () {
      return !!this.$store.state.config.company.payment;
    },
    workersEnabled () {
      return !!this.$store.state.config.company.workers;
    },
    servicesEnabled () {
      return !!this.$store.state.config.company.services;
    },
    serviceColor () {
      if (this.servicesEnabled && this.itemData.service && this.itemData.service.color) {
        return 'border-left-color: ' + this.itemData.service.color;
      }

      return false;
    },
    timeTo () {
      let time = this.itemData.time.split(':').map(n => parseInt(n, 10));
      let date = new Date(0, 0, 0, time[0], time[1], 0, 0);
      let offset = parseInt(this.itemData.duration, 10);
      date.setMinutes(date.getMinutes() + offset);

      return [date.getHours(), date.getMinutes()].map(n => n < 10 ? '0' + n : n).join(':');
    },
    reservationDate () {
      return CalendarUtils.humanDateFormat(this.itemData.date, false);
    },
    isCyclic () {
      return this.itemData.has_periodicity;
    },
    hasPrice () {
      return this.itemData.payment !== null;
    },
    price () {
      if (this.itemData.payment !== null) {
        let priceParts = this.itemData.payment.price.split('.');

        if (priceParts[1] === '00') {
          return priceParts[0];
        }

        return this.itemData.payment.price;
      }

      return false;
    },
    priceCurrency () {
      if (this.itemData.payment && this.itemData.payment.currency) {
        return this.itemData.payment.currency;
      } else if (this.itemData.configuration_payment) {
        return this.itemData.configuration_payment.currency;
      }

      return this.$root.defaultCurrency;
    },
    totalPrice () {
      return CurrencyUtils.getQuote(this.itemData.payment.price, this.priceCurrency);
    },
    paymentStatus () {
      if (this.itemData.payment.status === 'COMPLETED') {
        return this.$t('searchPopupReservation.paid');
      }

      if (this.itemData.payment.status === 'CASH') {
        return this.$t('common.ui.paidWithCash');
      }

      if (this.itemData.payment.status === 'CARD_REGISTERED') {
        return this.$t('searchPopupReservation.cardRegistered');
      }

      return this.$t('searchPopupReservation.unpaid');
    },
    paymentStatusClass () {
      if (['CASH', 'COMPLETED', 'CARD_REGISTERED'].indexOf(this.itemData.payment.status) > -1) {
        return 'is-paid';
      }

      return 'is-not-paid';
    },
    reservationDateStart () {
      if (this.itemData.start_at) {
        return CalendarUtils.getDayNameFromDate(this.itemData.start_at) + ', ' + CalendarUtils.humanDateFormat(this.itemData.start_at, false);
      }

      return '-';
    },
    reservationDateEnd () {
      if (this.itemData.end_at) {
        return CalendarUtils.getDayNameFromDate(this.itemData.end_at) + ', ' + CalendarUtils.humanDateFormat(this.itemData.end_at, false);
      }

      return '-';
    }
  },
  methods: {
    reject (e) {
      e.stopPropagation();

      if (this.isCyclic) {
        this.$bus.$emit('confirm-display', {
          title: this.$t('searchPopupReservation.cancelReservationTitle'),
          desc: this.$t('searchPopupReservation.cancelReservationDesc'),
          cancelLabel: this.$t('searchPopupReservation.cancelReservationCancelButton'),
          cancelClick: (notifyUser) => {
            this.rejectInquiry(true, notifyUser);
          },
          okLabel: this.$t('searchPopupReservation.cancelReservationOkButton'),
          okClick: (notifyUser) => {
            this.rejectInquiry(false, notifyUser);
          },
          closeButton: true,
          checkbox: true,
          checkboxLabel: this.$t('reservationForm.notifyClient'),
          checkboxDefaultValue: true
        });
      } else {
        this.$bus.$emit('confirm-display', {
          title: this.$t('searchPopupReservation.cancelReservationTitle'),
          desc: this.$t('searchPopupReservation.cancelReservationDesc2'),
          cancelLabel: this.$t('common.ui.close'),
          okLabel: this.$t('searchPopupReservation.cancelReservationOkButton2'),
          okClick: (notifyUser) => {
            this.rejectInquiry(false, notifyUser);
          },
          checkbox: true,
          checkboxLabel: this.$t('reservationForm.notifyClient'),
          checkboxDefaultValue: true
        });
      }
    },
    rejectInquiry (rejectFutureInquiries = false, notifyUser = false) {
      this.rejectInProgress = true;

      let configuration = {
        id: this.itemData.id,
        status: 5,
        notify_user: +notifyUser
      };

      if (rejectFutureInquiries) {
        configuration.periodicity = 1;
      }

      this.$http.post('/panel/general/set-inquiry-status', configuration).then(response => {
        this.$store.commit('global/removeInquiry', this.itemData.id);
        this.$bus.$emit('dashboard-reservations-list-reload-needed');
        this.finishRejectionProcess();
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/general/set-inquiry-status', this.$router, this.$bus, this.rejectInquiry.bind(this, rejectFutureInquiries));
        this.rejectInProgress = false;
      });
    },
    checkReservationList () {
      if (this.$store.state.config.company.waiting_list) {
        this.$http.post('/panel/reservations/get-reserve-list', {
          id: this.itemData.id
        }).then(response => {
          if (!response.data.data.length) {
            this.finishRejectionProcess();
          } else {
            let people = response.data.data.map(person => {
              let output = '<strong>' + person.name + '</strong>';

              if (person.phone) {
                output += ', ' + person.phone;
              }

              if (person.email) {
                output += ', ' + person.email;
              }

              return output;
            });
            let list = '<ul><li>' + people.join('</li><li>') + '</li></ul>';

            this.$bus.$emit('confirm-display', {
              title: this.$t('searchPopupReservation.youHaveWaitingListTitle'),
              desc: this.$t('searchPopupReservation.youHaveWaitingListDesc') + list,
              cancelLabel: this.$t('common.ui.closeNotNotify'),
              cancelClick: this.finishRejectionProcess,
              okLabel: this.$t('searchPopupReservation.youHaveWaitingListOkButton'),
              okClick: this.notifyPeople.bind(this)
            });
          }
        }).catch(error => {
          ErrorUtils.handleSubmitError(error, '/panel/reservations/get-reserve-list', this.$router, this.$bus, this.checkReservationList.bind(this));
          this.finishRejectionProcess();
        });
      } else {
        this.finishRejectionProcess();
      }
    },
    finishRejectionProcess (sendNotifications = false) {
      let msg = this.$t('searchPopupReservation.reservationCancelled');

      if (sendNotifications) {
        msg = this.$t('searchPopupReservation.reservationCancelledNotificationsSent');
      }

      this.rejectInProgress = false;
      this.$bus.$emit('notification-display', {
        type: 'success',
        message: msg
      });
      this.$bus.$emit('search-popup-refresh');
      this.$bus.$emit('calendar-layout-reload-needed');
    },
    notifyPeople (people) {
      this.$http.post('/panel/reservations/send-reserve-list', {
        id: this.itemData.id
      }).then(response => {
        this.finishRejectionProcess(true);
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/reservations/send-reserve-list', this.$router, this.$bus, this.notifyPeople.bind(this, people));
        this.finishRejectionProcess();
      });
    },
    showReservationDetails (e) {
      e.stopPropagation();
      this.$bus.$emit('show-reservation-details-popup', this.itemData.id, this.itemData.type);
    },
    stopEventPropagation (e) {
      e.stopPropagation();
      this.$bus.$emit('body-clicked');
    },
    clientNotPresent () {
      this.$bus.$emit('confirm-display', {
        title: this.$t('searchPopupReservation.clientNotPresentTitle'),
        desc: this.$t('searchPopupReservation.clientNotPresentDesc'),
        cancelLabel: this.$t('common.ui.cancel'),
        cancelClick: () => false,
        okLabel: this.$t('searchPopupReservation.clientNotPresentOkButton'),
        okClick: this.setPresentState,
        isDanger: true
      });
    },
    setPresentState () {
      this.presentInProgress = true;

      this.$http.post('/panel/attendances/status/' + this.itemData.id + '/2').then(response => {
        this.presentInProgress = false;
        this.$bus.$emit('notification-display', {
          type: 'success',
          message: this.$t('searchPopupReservation.clientSavedAsNotPresent')
        });
        this.$bus.$emit('search-popup-refresh');
        this.$bus.$emit('calendar-layout-reload-needed');
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/general/set-present-status', this.$router, this.$bus, this.setPresentState.bind(this));
        this.presentInProgress = false;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.reservation {
  align-items: center;
  border-bottom: 1px solid $color-border-light;
  border-left: 4px solid $color-border;
  border-right: 1px solid $color-border;
  display: flex;
  padding: .5rem 1rem .5rem 0;
  width: 100%;

  &:first-child {
    border-top: 1px solid $color-border-light;
  }

  .date-time {
    min-width: 260px;
    padding-left: 1rem;

    .date,
    .time {
      display: block;
      text-align: center;
      white-space: nowrap;
      width: 100%;
    }

    .date {
      font-size: 1.2rem;
      margin-bottom: .5rem;
    }

    .time .hour-value {
      font-size: 1.8rem;
    }
  }

  .duration-range {
    color: $color-text-light;
    font-size: 1.4rem;
    font-weight: 600;
  }

  .worker {
    margin-right: 1rem;
    position: relative;
  }

  .customer {
    position: relative;
    width: 40%;

    & > strong {
      font-size: 1.4rem;
      position: relative;
      top: -.3rem;
      white-space: normal;
      word-break: break-word;
    }
  }

  .phone,
  .email,
  .client-info {
    color: $color-text;
    font-size: 1.4rem;
    margin-left: .25rem;
    position: relative;
    top: .2rem;
    will-change: opacity;

    svg {
      margin-right: .5rem;
      position: relative;
      top: -.1rem;
    }

    &:hover {
      .tooltip {
        opacity: 1;
        top: -3rem;
      }
    }
  }

  .phone,
  .email {
    top: .3rem;
  }

  .client-info {
    cursor: pointer;
    top: .3rem;
  }

  .people-number {
    position: relative;
    top: .2rem;

    &-counter {
      align-items: center;
      background: $color-text-light;
      border: 1px solid $color-light;
      border-radius: 1rem;
      color: $color-light;
      display: inline-flex;
      font-size: 1rem;
      font-weight: bold;
      margin-left: -.7rem;
      padding: .1rem .5rem;
      position: relative;
      top: -.2rem;
    }
  }

  .tooltip {
    top: -2.5rem;

    &.tooltip-bottom {
      top: 2.8rem!important;
    }
  }

  .actions {
    align-items: center;
    display: flex;
    justify-content: center;
    margin-left: auto;

    .button {
      margin: 0 .5rem;

      &:first-child {
        margin-left: 0;
      }
    }
  }

  .additional-details {
    font-size: 1.2rem;
    margin-left: auto;
    padding: .5rem 1rem .5rem 0;
    width: 30%;
  }

  .price {
    font-size: 1.3rem;
    margin: 0;

    .payment-status {
      .is-paid {
        color: $color-success;
      }

      .is-not-paid {
        color: $color-danger;
      }
    }

    .payment-status,
    .total-price {
      color: $color-text-light;
      display: inline-block;
      font-size: 1.4rem;
      letter-spacing: -.025rem;
      margin: 1.5rem 0 0 0;
    }

    .service {
      cursor: pointer;
      display: block;
      font-size: 1.3rem;
      font-weight: bold;
      padding: 0 0 .75rem 0;
      position: relative;

      & ~ .payment-status,
      & ~ .total-price {
        font-size: 1.3rem;
        margin-top: 0;
      }

      &.no-price {
        margin-top: 0;
      }

      &:hover {
        .tooltip {
          opacity: 1;
          top: 50%;

          &.tooltip-top {
            top: -24px;
          }
        }
      }
    }
  }
}

@media (max-width: 900px) {
  .reservation {
    flex-wrap: wrap;
    justify-content: center;
    padding: 1rem;

    .actions,
    .additional-details,
    .date-time {
      width: 100%;
    }

    .customer {
      margin-bottom: 1rem;
      margin-top: 1rem;
      width: auto;
    }

    .additional-details {
      text-align: center;
    }

    .date-time {
      text-align: center;
    }

    .actions {
      .button {
        width: 20%;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/SearchPopupReservation.vue